import { NavLink } from 'react-router';

import { useCurrentUser } from '@core/auth';
import { useTranslation } from '@core/i18n';
import { Tooltip } from '@core/ui';
import { cn } from '@core/utils/cn';

import { NAV_SECTIONS, type NavItem } from './navigation';

interface SideNavProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

/**
 * Grouped, role-filtered navigation.
 *
 * Hiding an item the user cannot use is courtesy, not security: the route is guarded and
 * the API enforces the rule regardless.
 *
 * A section whose every item is filtered out disappears entirely, heading included --
 * otherwise a VIEWER sees an "Organization" heading with one link under it, which reads
 * as a broken menu rather than a restricted one.
 */
export function SideNav({ collapsed = false, onNavigate }: SideNavProps) {
  const me = useCurrentUser();
  const { t } = useTranslation();

  const visible = NAV_SECTIONS.filter((section) =>
    section.scope === 'platform' ? me.platformAdmin : Boolean(me.organization),
  )
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.role || me.can(item.role)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <nav aria-label={t.nav.main} className="flex flex-col gap-5 px-2 py-3">
      {visible.map((section) => (
        <div key={section.id}>
          {collapsed ? (
            // A hairline stands in for the heading so the grouping survives collapse.
            // Rendering the text at 11px in a 64px rail would just be unreadable noise.
            <div aria-hidden className="border-separator mx-2 mb-2 border-t first:border-0" />
          ) : (
            <h2 className="text-fg-subtle mb-1 px-3 text-[11px] font-semibold tracking-[0.06em] uppercase">
              {section.title(t)}
            </h2>
          )}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <NavItemLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function NavItemLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const label = item.label(t);

  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      // The accessible name must survive collapse: the visible text is gone, so the
      // title attribute alone would leave a screen reader announcing only "link".
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md text-[15px] transition-colors duration-150',
          // 44px minimum touch target in both states. `mx-auto` rather than relying on
          // the list to centre it: the item is a fixed 44px square inside a padded rail,
          // so without it the square sits at the left padding edge and every icon lands
          // a couple of pixels off the rail's centre line -- subtle, and immediately
          // obvious once the labels are gone and the icons are all you have.
          collapsed ? 'mx-auto h-11 w-11 justify-center' : 'h-11 px-3',
          'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          isActive
            ? 'bg-accent-tint text-accent ring-accent/20 font-medium ring-1 ring-inset'
            : 'text-fg-muted hover:bg-fill-subtle hover:text-fg',
        )
      }
    >
      <item.icon className="size-5 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}
